import { useState } from "react";
import { T } from "../utils/theme";
import { api, ENDPOINTS } from "../utils/api";
import { useToast, useModal } from "../hooks";
import {
  Card, SectionHeader, Modal, Field, LoadingBtn, AlertBox, ToastStack,
} from "../components/UI";
import { Zap, Plus, Shield, Lock } from "lucide-react";

/*
  CORRECTIONS vs doc API :

  1. POST /auth/register/admin (Auth + SuperAdmin)
     → ENDPOINTS.registerAdmin → "/auth/register/admin"
     Corps : { email, password, nom, prenom }
     Rôles autorisés : SUPERADMIN uniquement.

  2. La vérification du rôle `user?.role !== "superadmin"` est correcte
     car UserRole = "superadmin" | "admin" | "company" | "hacker".

  3. Pas de changements majeurs, corrections mineures de cohérence.
*/

const AdminManagementPage = ({ token, user }) => {
  const { toasts, show, remove } = useToast();
  const createModal = useModal();
  const [form, setForm]       = useState({ email: "", contact: "", nom: "", prenom: "" });
  const [creating, setCreating] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /*
    CORRECTION : POST /auth/register/admin (Auth + SuperAdmin)
    Le token du superadmin est requis dans l'Authorization header.
  */
  const createAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api(ENDPOINTS.registerAdmin, {
        method: "POST",
        body: JSON.stringify({
          email:    form.email,
          contact: form.contact,
          nom:      form.nom,
          prenom:   form.prenom,
        }),
      }, token); // token superadmin requis

      show("✅ Compte administrateur créé !");
      createModal.close();
     setForm({ email: "", contact: "", nom: "", prenom: "" });
    } catch (e) {
      // 403 Forbidden si pas superadmin, 409 Conflict si email déjà utilisé
      show(e.message || "Erreur lors de la création", "error");
    } finally {
      setCreating(false);
    }
  };

  // Accès restreint au rôle "superadmin"
  if (user?.role !== "superadmin") {
    return (
      <div className="fade-up" style={{
        padding: "24px 28px", flex: 1,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ textAlign: "center" }}>
          <Lock size={48} color={T.muted} style={{ opacity: .3, marginBottom: 16 }} />
          <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 18, color: T.text, marginBottom: 6 }}>
            Accès Restreint
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            Cette section est réservée aux Super Admins uniquement.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      <SectionHeader
        title="Gestion des Administrateurs"
        sub="Super Admin — Créez et gérez les comptes administrateurs"
        action={
          <button className="btn btn-primary" onClick={createModal.openWith}>
            <Plus size={14} /> Créer un Admin
          </button>
        }
      />

      <AlertBox type="warning" style={{ marginBottom: 22 }}>
        En tant que Super Admin, vous avez le contrôle total sur la plateforme.
        La création de comptes admins est irréversible — assurez-vous de la légitimité des accès accordés.
      </AlertBox>

      {/* Privilèges */}
      <div className="grid-2" style={{ marginBottom: 22 }}>
        <Card style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${T.blue}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Shield size={17} color={T.blue} />
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14 }}>Rôle Admin</div>
              <div style={{ fontSize: 11, color: T.muted }}>Permissions standards</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              "Voir et gérer les hackers",
              "Voir les entreprises",
              "Voir tous les programmes",
              "Voir tous les rapports",
              "Valider les KYC",
              "Demander un accès chiffré (dual control)",
              "Approuver les accès chiffrés",
            ].map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.textSub }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.blue, flexShrink: 0 }} />
                {p}
              </div>
            ))}
          </div>
        </Card>

        <Card style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${T.purple}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Zap size={17} color={T.purple} />
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14 }}>Rôle Super Admin</div>
              <div style={{ fontSize: 11, color: T.muted }}>Toutes les permissions Admin +</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              "Créer des comptes administrateurs",
              "Accès total à la plateforme",
              "Gestion des configurations système",
              "Audit trail complet",
              "Contrôle des accès chiffrés",
              "Statistiques avancées",
              "Gestion des rôles",
            ].map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.textSub }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.purple, flexShrink: 0 }} />
                {p}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Infos système */}
      <Card style={{ padding: 22 }}>
        <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Informations Système</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
          {[
            { l: "Plateforme",   v: "200Bounty v2.0"   },
            { l: "API Version",  v: "REST v1"           },
            { l: "Chiffrement",  v: "RSA-2048 + AES"   },
            { l: "Auth",         v: "JWT Bearer Token"  },
            { l: "KYC",          v: "Biométrique + Docs"},
            { l: "Dual Control", v: "Activé"            },
          ].map((s, i) => (
            <div key={i} style={{ background: T.bg, borderRadius: 9, padding: "11px 14px" }}>
              <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 4 }}>
                {s.l.toUpperCase()}
              </div>
              <div style={{ fontSize: 13, color: T.cyan, fontFamily: T.mono }}>{s.v}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Modal créer admin */}
      <Modal open={createModal.open} onClose={createModal.close} title="Créer un Administrateur" width={480}>
        <AlertBox type="warning" style={{ marginBottom: 18 }}>
          Réservé au Super Admin. Le nouveau compte aura accès à toutes les fonctions admin de la plateforme.
          Endpoint : <code style={{ color: T.cyan }}>POST /auth/register/admin</code>
        </AlertBox>
        <form onSubmit={createAdmin}>
          <div className="grid-2">
            <Field label="Prénom">
              <input
                placeholder="Jean"
                value={form.prenom}
                onChange={e => set("prenom", e.target.value)}
                required
              />
            </Field>
            <Field label="Nom">
              <input
                placeholder="Dupont"
                value={form.nom}
                onChange={e => set("nom", e.target.value)}
                required
              />
            </Field>
          </div>
          <Field label="Email">
            <input
              type="email"
              placeholder="admin@200bounty.com"
              value={form.email}
              onChange={e => set("email", e.target.value)}
              required
            />
                    </Field>
                  <Field label="Contact">
            <input
              type="text"
              placeholder="+2250700000000"
              value={form.contact}
              onChange={e => set("contact", e.target.value)}
              required
            />
          </Field>
          <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={createModal.close}
            >
              Annuler
            </button>
            <LoadingBtn
              loading={creating}
              className="btn btn-primary"
              style={{ flex: 1, justifyContent: "center" }}
            >
              <Plus size={13} /> Créer le compte
            </LoadingBtn>
          </div>
        </form>
      </Modal>

      <ToastStack toasts={toasts} remove={remove} />
    </div>
  );
};

export default AdminManagementPage;